import React, { useState, useEffect } from 'react';
import { FaUsers, FaHeart, FaComment, FaFlag, FaDollarSign, FaCalendarAlt, FaUserShield } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const testDatabaseConnection = async () => {
    try {
      console.log('🔍 Testing database connection...');
      const response = await adminAPI.testDatabase();
      console.log('📊 Database test:', response);

      const { summary, tests, sampleData } = response;

      let message = `📊 Database Tables Test Results:\n\n`;
      message += `Total Tables: ${summary.totalTables}\n`;
      message += `Existing: ${summary.existingTables}\n`;
      message += `Missing: ${summary.missingTables}\n`;
      message += `With Data: ${summary.tablesWithData}\n\n`;

      if (summary.missingTables > 0) {
        message += `❌ Missing Tables:\n`;
        Object.entries(tests).forEach(([table, info]) => {
          if (!info.exists) {
            message += `- ${table}: ${info.error}\n`;
          }
        });
        message += `\n`;
      }

      message += `📋 Sample Data:\n`;
      if (sampleData.users && sampleData.users.length > 0) {
        message += `Users: ${sampleData.users[0].full_name} (${sampleData.users[0].email})\n`;
      }
      if (sampleData.roles && sampleData.roles.length > 0) {
        message += `Roles: ${sampleData.roles[0].display_name} (${sampleData.roles[0].name})\n`;
      }
      if (sampleData.user_roles && sampleData.user_roles.length > 0) {
        message += `User Roles: ${sampleData.user_roles[0].name} role assigned\n`;
      }

      alert(message);
    } catch (error) {
      console.error('💥 Database test error:', error);
      alert('❌ Database test failed!\n\nError: ' + error.message);
    }
  };

  const testDatabaseTables = async () => {
    // This is now handled by testDatabaseConnection
    await testDatabaseConnection();
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching dashboard stats...');

      const response = await adminAPI.getDashboardStats();
      console.log('📊 API Response:', response);

      if (response.success) {
        console.log('✅ Stats loaded:', response.stats);
        setStats(response.stats);
        setError(null);
      } else {
        console.error('❌ API Error:', response.message);
        setError(response.message || 'Failed to load dashboard statistics');
      }
    } catch (error) {
      console.error('💥 Network Error:', error);
      console.error('💥 Error response:', error.response);
      console.error('💥 Error message:', error.message);

      let errorMessage = 'Failed to load dashboard statistics';

      if (error.status === 401) {
        errorMessage = 'Unauthorized - Please login again';
      } else if (error.status === 403) {
        errorMessage = 'Access denied - Admin role required';
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Network error - Check API connection';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <div className={`bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10 ${color}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 truncate">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1 truncate">{subtitle}</p>}
        </div>
        <div className="p-2 sm:p-3 rounded-full bg-gray-50 ml-2 sm:ml-4 flex-shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <div className="mt-4">
              <button
                onClick={fetchDashboardStats}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded-md text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 xl:space-y-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Welcome back, <span className="font-medium">{user?.full_name}</span> ({user?.role?.name})
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={fetchDashboardStats}
            className="bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">🔄</span>
          </button>
          <button
            onClick={testDatabaseConnection}
            className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <span className="hidden sm:inline">Test DB</span>
            <span className="sm:hidden">🧪</span>
          </button>
          <button
            onClick={testDatabaseTables}
            className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors"
          >
            <span className="hidden sm:inline">Check Tables</span>
            <span className="sm:hidden">📋</span>
          </button>
        </div>
      </div>

      {/* Role Info */}
      {user?.role && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-sm p-4 sm:p-6 text-white">
          <div className="flex items-center">
            <FaUserShield className="w-6 h-6 sm:w-8 sm:h-8 mr-3 sm:mr-4 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-semibold">{user.role.name.toUpperCase()}</h3>
              <p className="text-blue-100 text-xs sm:text-sm truncate">
                {user.role.permissions ? Object.keys(user.role.permissions).filter(p => user.role.permissions[p]).join(', ') : 'No permissions'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          icon={FaUsers}
          title="Total Users"
          value={stats?.totalUsers?.toLocaleString() || '0'}
          subtitle="Registered users"
        />
        <StatCard
          icon={FaUsers}
          title="Active Users"
          value={stats?.activeUsers?.toLocaleString() || '0'}
          subtitle="Last 7 days"
        />
        <StatCard
          icon={FaUsers}
          title="VIP Users"
          value={stats?.vipUsers?.toLocaleString() || '0'}
          subtitle="Premium members"
        />
        <StatCard
          icon={FaHeart}
          title="Total Matches"
          value={stats?.totalMatches?.toLocaleString() || '0'}
          subtitle="Profile likes"
        />
        <StatCard
          icon={FaComment}
          title="Total Messages"
          value={stats?.totalMessages?.toLocaleString() || '0'}
          subtitle="Sent messages"
        />
        <StatCard
          icon={FaFlag}
          title="Pending Reports"
          value={stats?.pendingReports?.toLocaleString() || '0'}
          subtitle="Awaiting review"
        />
        <StatCard
          icon={FaDollarSign}
          title="Today's Revenue"
          value={`$${stats?.todayRevenue?.toFixed(2) || '0.00'}`}
          subtitle="24 hours"
        />
        <StatCard
          icon={FaDollarSign}
          title="Monthly Revenue"
          value={`$${stats?.monthlyRevenue?.toFixed(2) || '0.00'}`}
          subtitle="30 days"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FaUsers className="w-6 h-6 text-blue-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">Manage Users</div>
            <div className="text-xs text-gray-500">View and edit user accounts</div>
          </button>
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FaFlag className="w-6 h-6 text-orange-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">Review Reports</div>
            <div className="text-xs text-gray-500">Handle user reports</div>
          </button>
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FaDollarSign className="w-6 h-6 text-green-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">Financial Overview</div>
            <div className="text-xs text-gray-500">View transactions and revenue</div>
          </button>
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FaCalendarAlt className="w-6 h-6 text-purple-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">System Settings</div>
            <div className="text-xs text-gray-500">Configure application settings</div>
          </button>
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="text-center py-8 text-gray-500">
          <FaCalendarAlt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Recent activity monitoring coming soon...</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;